<template>
  <v-dialog ref="saleCheckDialog" v-model="dialog" max-width="900" scrollable v-drag-dialog content-class="draggable-dialog">
    <v-card class="rounded-xl sale-check-dialog">
      <CardToolbarDialog :title="dialogTitle" title-id="backoffice-sale-check-title" icon="mdi-cash-register" @cancel="close" />
      <v-divider />
      <div class="sale-check-dialog__body pa-4 pa-sm-5">
        <div class="mb-4">
          <h3 class="text-subtitle-1 font-weight-bold">{{ location && location.name }}</h3>
          <p class="text-caption admin-muted mb-0">{{ scopeLabel }}</p>
        </div>
        <v-skeleton-loader v-if="loading" type="article, list-item-two-line@4" aria-label="Loading sale check" />
        <v-alert v-else-if="errorMessage" type="error" text class="rounded-lg mb-0" role="alert"><span class="text--primary">{{ errorMessage }}</span></v-alert>
        <template v-else-if="report">
          <div v-if="!report.shiftCount" class="text-center py-8">
            <v-icon :color="actionColor" size="40">mdi-cash-register</v-icon>
            <h3 class="text-subtitle-1 font-weight-bold mt-3">No shifts for this period</h3>
            <p class="text-body-2 admin-muted mb-0">There is no register activity assigned to these business dates.</p>
          </div>
          <template v-else>
            <p v-if="!shiftId" class="text-caption admin-muted mb-4">All registers · {{ report.shiftCount }} {{ report.shiftCount === 1 ? 'shift' : 'shifts' }}. Includes each selected business date's full shifts.</p>
            <v-card v-if="shiftReceipt" outlined class="rounded-lg pa-4 mb-4">
              <div class="sale-check-heading mb-3">
                <h4 class="text-subtitle-2 font-weight-bold">{{ shiftReceipt.CashDrawer && shiftReceipt.CashDrawer.name || 'Register' }} · Shift #{{ shiftReceipt.id }}</h4>
                <span class="text-caption text-capitalize">{{ shiftReceipt.status }}</span>
              </div>
              <p class="text-caption admin-muted mb-3">Business date {{ shiftReceipt.businessDate }} · {{ receiptTimezone }}</p>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <h5 class="text-subtitle-2 mb-2">Opening</h5>
                  <dl class="sale-check-values">
                    <div><dt>Opened</dt><dd>{{ formatTime(shiftReceipt.openTime) }}</dd></div>
                    <div><dt>Employee</dt><dd>{{ employeeName(shiftReceipt.OpeningEmployee) }}</dd></div>
                    <div><dt>Starting cash</dt><dd class="sale-check-values__money">{{ recordedMoney(shiftReceipt.openBalance) }}</dd></div>
                  </dl>
                  <p v-if="shiftReceipt.openNote" class="text-body-2 mt-2 mb-0 sale-check-note"><strong>Opening note:</strong> {{ shiftReceipt.openNote }}</p>
                </v-col>
                <v-col cols="12" sm="6">
                  <h5 class="text-subtitle-2 mb-2">Closing</h5>
                  <p v-if="shiftReceipt.status !== 'closed'" class="text-body-2 admin-muted mb-0">This shift is still open.</p>
                  <template v-else>
                    <dl class="sale-check-values">
                      <div><dt>Closed</dt><dd>{{ formatTime(shiftReceipt.closeTime) }}</dd></div>
                      <div><dt>Employee</dt><dd>{{ employeeName(shiftReceipt.ClosingEmployee) }}</dd></div>
                      <div><dt>System cash count</dt><dd class="sale-check-values__money">{{ recordedMoney(shiftReceipt.closeSystemCashCount) }}</dd></div>
                      <div><dt>Employee cash count</dt><dd class="sale-check-values__money">{{ recordedMoney(shiftReceipt.closeCashCount) }}</dd></div>
                      <div><dt>Adjustment</dt><dd class="sale-check-values__money">{{ recordedMoney(shiftReceipt.adjustmentAmount) }}</dd></div>
                      <div><dt>Cash left in register</dt><dd class="sale-check-values__money">{{ recordedMoney(shiftReceipt.leavingCash) }}</dd></div>
                    </dl>
                    <p v-if="shiftReceipt.closeNote" class="text-body-2 mt-2 mb-0 sale-check-note"><strong>Closing note:</strong> {{ shiftReceipt.closeNote }}</p>
                  </template>
                </v-col>
              </v-row>
              <v-expansion-panels v-if="cashCountSections.length" flat multiple class="mt-3">
                <v-expansion-panel v-for="section in cashCountSections" :key="section.title">
                  <v-expansion-panel-header class="px-0 text-body-2 font-weight-medium">{{ section.title }}</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <dl class="sale-check-values mb-3">
                      <div><dt>System count</dt><dd class="sale-check-values__money">{{ recordedMoney(section.record.systemCountTotal) }}</dd></div>
                      <div><dt>Employee count</dt><dd class="sale-check-values__money">{{ recordedMoney(section.record.employeeCountTotal) }}</dd></div>
                      <div><dt>Difference (system minus employee)</dt><dd class="sale-check-values__money">{{ recordedMoney(section.record.difference) }}</dd></div>
                    </dl>
                    <div v-if="section.denominations.length" class="sale-check-count-table-wrap" tabindex="0" role="region" :aria-label="`${section.title} denominations`">
                      <table class="sale-check-count-table">
                        <thead><tr><th scope="col">Denomination</th><th scope="col">Quantity</th><th scope="col">Amount</th></tr></thead>
                        <tbody><tr v-for="(row, index) in section.denominations" :key="index"><td>{{ row.denomination | currency(currencySymbol) }}</td><td>{{ row.quantity }}</td><td>{{ row.denomination * row.quantity | currency(currencySymbol) }}</td></tr></tbody>
                      </table>
                    </div>
                    <p v-else class="text-body-2 admin-muted mb-0">Denominations were not recorded.</p>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-card outlined class="rounded-lg pa-4" height="100%">
                  <div class="sale-check-heading mb-3"><h4 class="text-subtitle-2 font-weight-bold">Total sale</h4><strong class="text-h6" :class="primaryTextClass">{{ report.Summary.revenue | currency(currencySymbol) }}</strong></div>
                  <p v-if="aggregateOnly" class="text-body-2 admin-muted">A detailed sales breakdown is unavailable for one or more selected shifts. The total sale is still available.</p>
                  <dl v-else class="sale-check-values">
                    <div v-for="row in saleRows" :key="row.key"><dt>{{ row.label }}</dt><dd class="sale-check-values__money">{{ row.value | currency(currencySymbol) }}</dd></div>
                  </dl>
                  <p class="text-caption admin-muted mt-3 mb-0">Includes tax and payment activity.</p>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card outlined class="rounded-lg pa-4" height="100%">
                  <h4 class="text-subtitle-2 font-weight-bold mb-3">{{ shiftId ? 'Cash summary' : 'Cash across selected shifts' }}</h4>
                  <dl class="sale-check-values">
                    <div v-for="row in cashRows" :key="row.key"><dt>{{ row.label }}</dt><dd class="sale-check-values__money">{{ row.value | currency(currencySymbol) }}</dd></div>
                    <div class="font-weight-bold"><dt>Expected cash{{ shiftId ? '' : ' across shifts' }}</dt><dd class="sale-check-values__money">{{ report.Summary.cash.total | currency(currencySymbol) }}</dd></div>
                  </dl>
                  <p v-if="!shiftId" class="text-caption admin-muted mt-3 mb-0">Starting and expected cash are totals across shifts, not the cash currently in your registers.</p>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card outlined class="rounded-lg pa-4" height="100%">
                  <h4 class="text-subtitle-2 font-weight-bold mb-3">Payment breakdown</h4>
                  <dl v-if="paymentRows.length" class="sale-check-values"><div v-for="payment in paymentRows" :key="payment.id || payment.name"><dt>{{ payment.name }}</dt><dd class="sale-check-values__money">{{ payment.total | currency(currencySymbol) }}</dd></div></dl>
                  <p v-else class="text-body-2 admin-muted mb-0">No payments found.</p>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6">
                <v-card outlined class="rounded-lg pa-4" height="100%">
                  <h4 class="text-subtitle-2 font-weight-bold mb-3">Activity</h4>
                  <dl class="sale-check-values">
                    <div><dt>Invoice count</dt><dd>{{ report.Summary.invoiceCount.toLocaleString() }}</dd></div>
                    <div><dt>Open invoices</dt><dd>{{ aggregateOnly ? 'Unavailable' : report.Summary.openInvoices.length.toLocaleString() }}</dd></div>
                    <div><dt>Transfers</dt><dd>{{ report.Summary.transfer.count.toLocaleString() }}</dd></div>
                    <div><dt>Transfer total</dt><dd class="sale-check-values__money">{{ report.Summary.transfer.total | currency(currencySymbol) }}</dd></div>
                    <div><dt>No-sale drawer opens</dt><dd>{{ aggregateOnly ? 'Unavailable' : report.Summary.openCashDrawCount.toLocaleString() }}</dd></div>
                    <div><dt>Tax collected</dt><dd class="sale-check-values__money">{{ report.Summary.taxCollected | currency(currencySymbol) }}</dd></div>
                  </dl>
                </v-card>
              </v-col>
              <v-col cols="12">
                <v-card outlined class="rounded-lg pa-4">
                  <h4 class="text-subtitle-2 font-weight-bold mb-3">Sales by category</h4>
                  <p v-if="aggregateOnly" class="text-body-2 admin-muted mb-0">Category details are unavailable for one or more selected shifts.</p>
                  <dl v-else-if="report.CategoryRows.length" class="sale-check-values"><div v-for="(category, index) in report.CategoryRows" :key="`${category.name}-${index}`"><dt>{{ category.name }}</dt><dd class="sale-check-values__money">{{ category.total | currency(currencySymbol) }}</dd></div></dl>
                  <p v-else class="text-body-2 admin-muted mb-0">No category sales found.</p>
                </v-card>
              </v-col>
            </v-row>
          </template>
        </template>
      </div>
      <v-divider />
      <v-card-actions class="sale-check-dialog__footer px-4 py-3">
        <v-btn text :color="actionColor" min-height="44" :disabled="loading" @click="loadReport">{{ errorMessage ? 'Try again' : 'Refresh' }}</v-btn>
        <v-spacer />
        <v-btn outlined :color="actionColor" min-height="44" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import reportService, { saleBreakdownRows } from "@/modules/Reports/service";

export default {
  name: "BackofficeSaleCheckDialog",
  props: {
    value: { type: Boolean, default: false },
    location: { type: Object, default: null },
    dateRange: { type: Object, default: () => ({}) },
    timezone: { type: String, default: "UTC" },
    shiftId: { type: [Number, String], default: null },
  },
  data() { return { report: null, loading: false, errorMessage: "", requestId: 0 }; },
  computed: {
    dialog: { get() { return this.value; }, set(value) { this.$emit("input", value); } },
    actionColor() { return this.$vuetify.theme.dark ? "primary lighten-4" : "primary"; },
    primaryTextClass() { return this.$vuetify.theme.dark ? "primary--text text--lighten-4" : "primary--text"; },
    dialogTitle() { return this.shiftId ? (this.shiftReceipt && this.shiftReceipt.status === "closed" ? "Shift closing receipt" : "Shift sale check") : "Sale check"; },
    shiftReceipt() { return this.shiftId && this.report && Array.isArray(this.report.Shifts) ? this.report.Shifts.find((shift) => String(shift.id) === String(this.shiftId)) : null; },
    receiptTimezone() { return this.report && this.report.location.timeZone || this.timezone; },
    cashCountSections() {
      if (!this.shiftReceipt) return [];
      return [{ title: "Opening cash count", record: this.shiftReceipt.OpenCashCount }, { title: "Closing cash count", record: this.shiftReceipt.ClosedCashCount }].filter((section) => section.record).map((section) => {
        let denominations = [];
        try { denominations = typeof section.record.data === "string" ? JSON.parse(section.record.data) : section.record.data; } catch (error) { denominations = []; }
        return { ...section, denominations: Array.isArray(denominations) ? denominations.filter((row) => Number.isFinite(Number(row.denomination)) && Number.isFinite(Number(row.quantity))) : [] };
      });
    },
    currencySymbol() { return (this.report && this.report.location.currencySymbol) || (this.location && this.location.currencySymbol) || "$"; },
    requestKey() { return [this.location && this.location.id, this.shiftId, this.dateRange.start, this.dateRange.end, this.timezone].join("|"); },
    loadKey() { return this.value ? this.requestKey : ""; },
    scopeLabel() {
      if (this.shiftId) return `Shift #${this.shiftId}`;
      return `Business dates ${this.dateRange.start || ""} to ${this.dateRange.end || ""} · All registers`;
    },
    aggregateOnly() { return !!(this.report && (this.report.aggregateOnly || this.report.Summary.aggregateOnly)); },
    saleRows() {
      return this.report ? saleBreakdownRows(this.report.SaleBreakdown, this.aggregateOnly) : [];
    },
    cashRows() {
      if (!this.report) return [];
      const labels = { starting: "Starting cash", received: "Cash received", refunded: "Cash refunded", addcash: "Cash added", cashpickup: "Cash pickups", expense: "Expenses", purchase: "Purchases", employeeloan: "Employee loans", employeeloanpayment: "Employee loan repayments" };
      return Object.entries(this.report.Summary.cash.data).map(([key, value]) => ({ key, label: labels[key] || key, value })).filter((row) => ["starting", "received", "refunded"].includes(row.key) || Number(row.value) !== 0);
    },
    paymentRows() { return this.report ? this.report.PaymentMethods.filter((method) => Number(method.total) !== 0) : []; },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (!open) return;
        this.$nextTick(() => {
          // Vuetify 2 does not forward attributes to its detached dialog container.
          const content = this.$refs.saleCheckDialog && this.$refs.saleCheckDialog.$refs.content;
          if (content) content.setAttribute("aria-labelledby", "backoffice-sale-check-title");
        });
      },
    },
    loadKey: {
      immediate: true,
      handler(key) {
        if (key) this.loadReport();
        else { this.requestId += 1; this.loading = false; this.report = null; this.errorMessage = ""; }
      },
    },
  },
  beforeDestroy() { this.requestId += 1; },
  methods: {
    close() { this.$emit("input", false); },
    recordedMoney(value) { return value == null ? "Not recorded" : this.$options.filters.currency(value, this.currencySymbol); },
    employeeName(employee) { return employee ? [employee.firstName, employee.lastName].filter(Boolean).join(" ") || "Not recorded" : "Not recorded"; },
    formatTime(value) { return value ? this.$moment(value).tz(this.receiptTimezone).format("MMM D, YYYY h:mm A") : "Not recorded"; },
    async loadReport() {
      const requestId = ++this.requestId;
      this.report = null;
      this.errorMessage = "";
      if (!this.location || !this.location.id) { this.loading = false; this.errorMessage = "Select a location to view its sale check."; return; }
      this.loading = true;
      try {
        const params = { LocationId: this.location.id };
        if (this.shiftId) params.ShiftId = this.shiftId;
        else Object.assign(params, this.dateRange, { timezone: this.timezone });
        const response = await reportService.Sales.getSaleCheck(params);
        if (requestId !== this.requestId) return;
        const data = response && response.data;
        if (!data || !data.Summary || (!data.SaleBreakdown && !(data.aggregateOnly || data.Summary.aggregateOnly)) || !Array.isArray(data.PaymentMethods) || !Array.isArray(data.CategoryRows)) throw new Error("Invalid sale check");
        this.report = data;
      } catch (error) {
        if (requestId === this.requestId) this.errorMessage = "Sale check could not be loaded. Try again or select a shorter period.";
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.sale-check-dialog { display: flex; flex-direction: column; max-height: calc(100dvh - 48px); overflow: hidden; }
.sale-check-dialog__body { flex: 1 1 auto; min-height: 0; overflow-y: auto; }
.sale-check-dialog__footer { flex: 0 0 auto; }
.sale-check-heading { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; }
.sale-check-values { margin: 0; }
.sale-check-values > div { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; padding: 7px 0; }
.sale-check-values dt { min-width: 0; overflow-wrap: anywhere; }
.sale-check-values dd { margin: 0; text-align: right; max-width: 65%; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.sale-check-values .sale-check-values__money { white-space: nowrap; flex-shrink: 0; max-width: none; }
.sale-check-note { white-space: pre-wrap; overflow-wrap: anywhere; }
.sale-check-count-table-wrap { overflow-x: auto; }
.sale-check-count-table { width: 100%; border-collapse: collapse; font-size: .875rem; }
.sale-check-count-table th, .sale-check-count-table td { text-align: right; padding: 6px; white-space: nowrap; }
.sale-check-count-table th:first-child, .sale-check-count-table td:first-child { text-align: left; }
@media (max-width: 599px) { .sale-check-dialog { max-height: calc(100dvh - 32px); } }
</style>
